import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Users,
  Activity,
  TrendingUp,
  Calendar,
  Download,
  FileSpreadsheet,
  FileText,
  Loader2,
  User,
  Heart,
  Droplets,
  Scale,
  Clock,
  RefreshCw
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AgentActivityDashboard = ({ language = 'fr' }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [exporting, setExporting] = useState(false);

  // Preset date ranges
  const setPresetRange = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  };

  const setThisMonth = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(now.toISOString().split('T')[0]);
  };

  const setThisYear = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), 0, 1);
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(now.toISOString().split('T')[0]);
  };

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      const response = await axios.get(`${API_URL}/health/stats/agents?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching agent stats:', error);
      toast.error(language === 'fr' ? 'Erreur de chargement' : 'Loading error');
    } finally {
      setLoading(false);
    }
  }, [token, startDate, endDate, language]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      params.append('format', format);
      
      const response = await axios.get(`${API_URL}/health/stats/agents/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = format === 'pdf' 
        ? `SADAHealth_Activite_Agents_${new Date().toISOString().split('T')[0]}.pdf`
        : `SADAHealth_Activite_Agents_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(language === 'fr' ? 'Export réussi' : 'Export successful');
    } catch (error) {
      console.error('Export error:', error);
      toast.error(language === 'fr' ? 'Erreur d\'export' : 'Export error');
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Activity className="w-6 h-6 text-blue-600" />
            {language === 'fr' ? 'Activité des Agents - SADAHealth' : 'Agent Activity - SADAHealth'}
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {language === 'fr' ? 'Suivi des saisies et mesures par agent' : 'Track entries and measurements by agent'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => handleExport('excel')}
            disabled={exporting || !stats?.agents?.length}
            className="border-green-300 text-green-700 hover:bg-green-50"
            data-testid="export-excel-btn"
          >
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />}
            Excel
          </Button>
          <Button
            variant="outline"
            onClick={() => handleExport('pdf')}
            disabled={exporting || !stats?.agents?.length}
            className="border-red-300 text-red-700 hover:bg-red-50"
            data-testid="export-pdf-btn"
          >
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
            PDF
          </Button>
        </div>
      </div>

      {/* Date Filters */}
      <Card className="border-blue-100">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">
                {language === 'fr' ? 'Période :' : 'Period:'}
              </span>
            </div>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-40"
              data-testid="start-date-input"
            />
            <span className="text-gray-500">→</span>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-40"
              data-testid="end-date-input"
            />
            <Button variant="outline" size="sm" onClick={fetchStats} data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4" />
            </Button>
            <div className="flex flex-wrap gap-2 border-l pl-4 ml-2">
              <Button variant="ghost" size="sm" onClick={() => setPresetRange(7)}>7j</Button>
              <Button variant="ghost" size="sm" onClick={() => setPresetRange(30)}>30j</Button>
              <Button variant="ghost" size="sm" onClick={setThisMonth}>
                {language === 'fr' ? 'Ce mois' : 'This month'}
              </Button>
              <Button variant="ghost" size="sm" onClick={setThisYear}>
                {language === 'fr' ? 'Cette année' : 'This year'}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => { setStartDate(''); setEndDate(''); }}>
                {language === 'fr' ? 'Tout' : 'All'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {stats?.summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-blue-100 bg-blue-50/50">
            <CardContent className="p-4 text-center">
              <Users className="w-8 h-8 mx-auto mb-2 text-blue-600" />
              <p className="text-3xl font-bold text-gray-900">{stats.summary.total_agents}</p>
              <p className="text-sm text-gray-500">{language === 'fr' ? 'Agents actifs' : 'Active agents'}</p>
            </CardContent>
          </Card>
          <Card className="border-green-100 bg-green-50/50">
            <CardContent className="p-4 text-center">
              <Activity className="w-8 h-8 mx-auto mb-2 text-green-600" />
              <p className="text-3xl font-bold text-gray-900">{stats.summary.total_measurements}</p>
              <p className="text-sm text-gray-500">{language === 'fr' ? 'Mesures totales' : 'Total measurements'}</p>
            </CardContent>
          </Card>
          <Card className="border-purple-100 bg-purple-50/50">
            <CardContent className="p-4 text-center">
              <User className="w-8 h-8 mx-auto mb-2 text-purple-600" />
              <p className="text-3xl font-bold text-gray-900">{stats.summary.total_patients_created}</p>
              <p className="text-sm text-gray-500">{language === 'fr' ? 'Patients créés' : 'Patients created'}</p>
            </CardContent>
          </Card>
          <Card className="border-orange-100 bg-orange-50/50">
            <CardContent className="p-4 text-center">
              <TrendingUp className="w-8 h-8 mx-auto mb-2 text-orange-600" />
              <p className="text-3xl font-bold text-gray-900">{stats.summary.total_unique_patients_measured}</p>
              <p className="text-sm text-gray-500">{language === 'fr' ? 'Patients mesurés' : 'Patients measured'}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Agents Table */}
      <Card className="border-blue-100">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-600" />
            {language === 'fr' ? 'Détail par Agent' : 'Agent Details'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : !stats?.agents?.length ? (
            <div className="text-center py-12 text-gray-500">
              <Users className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>{language === 'fr' ? 'Aucune activité trouvée pour cette période' : 'No activity found for this period'}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="agents-table">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="text-left p-3 font-semibold text-gray-700">
                      {language === 'fr' ? 'Agent' : 'Agent'}
                    </th>
                    <th className="text-center p-3 font-semibold text-gray-700">
                      <span className="flex items-center justify-center gap-1">
                        <User className="w-4 h-4" />
                        {language === 'fr' ? 'Patients créés' : 'Created'}
                      </span>
                    </th>
                    <th className="text-center p-3 font-semibold text-gray-700">
                      <span className="flex items-center justify-center gap-1">
                        <Activity className="w-4 h-4" />
                        {language === 'fr' ? 'Mesures' : 'Measurements'}
                      </span>
                    </th>
                    <th className="text-center p-3 font-semibold text-gray-700">
                      {language === 'fr' ? 'Patients uniques' : 'Unique patients'}
                    </th>
                    <th className="text-center p-3 font-semibold text-gray-700">
                      <span className="flex items-center justify-center gap-1">
                        <Heart className="w-4 h-4 text-red-500" />
                        {language === 'fr' ? 'Tension' : 'BP'}
                      </span>
                    </th>
                    <th className="text-center p-3 font-semibold text-gray-700">
                      <span className="flex items-center justify-center gap-1">
                        <Droplets className="w-4 h-4 text-blue-500" />
                        {language === 'fr' ? 'Glycémie' : 'Glucose'}
                      </span>
                    </th>
                    <th className="text-center p-3 font-semibold text-gray-700">
                      <span className="flex items-center justify-center gap-1">
                        <Scale className="w-4 h-4 text-green-500" />
                        {language === 'fr' ? 'Poids' : 'Weight'}
                      </span>
                    </th>
                    <th className="text-center p-3 font-semibold text-gray-700">
                      <span className="flex items-center justify-center gap-1">
                        <Clock className="w-4 h-4" />
                        {language === 'fr' ? 'Dernière activité' : 'Last activity'}
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {stats.agents.map((agent, index) => (
                    <tr 
                      key={agent.agent_id} 
                      className={`border-b border-gray-100 hover:bg-blue-50/50 transition-colors ${index === 0 ? 'bg-yellow-50' : ''}`}
                      data-testid={`agent-row-${agent.agent_id}`}
                    >
                      <td className="p-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-semibold">
                            {agent.agent_name?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{agent.agent_name}</p>
                            {index === 0 && (
                              <Badge className="bg-yellow-100 text-yellow-800 text-xs">
                                {language === 'fr' ? 'Top agent' : 'Top agent'}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="p-3 text-center">
                        <span className="font-semibold text-purple-600">{agent.patients_created}</span>
                      </td>
                      <td className="p-3 text-center">
                        <span className="font-bold text-lg text-gray-900">{agent.total_measurements}</span>
                      </td>
                      <td className="p-3 text-center">
                        <span className="text-gray-600">{agent.unique_patients_measured}</span>
                      </td>
                      <td className="p-3 text-center">
                        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                          {agent.bp_measurements}
                        </Badge>
                      </td>
                      <td className="p-3 text-center">
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                          {agent.glycemia_measurements}
                        </Badge>
                      </td>
                      <td className="p-3 text-center">
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                          {agent.weight_measurements}
                        </Badge>
                      </td>
                      <td className="p-3 text-center text-gray-500 text-sm">
                        {formatDate(agent.last_activity_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AgentActivityDashboard;
